import { useEffect, useState, useCallback } from 'react';
import { FaCheck, FaTimes, FaExchangeAlt, FaTrash, FaUser, FaBox, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaClipboardList, FaInfoCircle, FaBoxOpen, FaSearch, FaSync } from 'react-icons/fa';
import AssistantSidebar from './AssistantSidebar';
import * as assetUserService from '../services/assetUserService';
import { API_BASE_URL } from '../config/api';
import '../styles/AssetManagement.css';

const AssetManagement = () => {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [activeTab, setActiveTab] = useState('requests');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [approvalData, setApprovalData] = useState({
    remarks: ''
  });
  const [allAssets, setAllAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetStatusFilter, setAssetStatusFilter] = useState('');

  const fetchAllAssets = useCallback(async () => {
    setAssetsLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('viewAll', 'true');
      if (assetSearch) params.append('search', assetSearch);
      if (assetStatusFilter) params.append('status', assetStatusFilter);
      const response = await fetch(
        `${API_BASE_URL}/api/assets?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await response.json();
      setAllAssets(result.success ? (result.data || []) : []);
    } catch (err) {
      console.error('Error fetching all assets:', err);
      setAllAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  }, [assetSearch, assetStatusFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'requests') {
        const data = await assetUserService.getPendingRequests(null, 'pending');
        setPendingRequests(data.requests || []);
      } else if (activeTab === 'assignments') {
        const data = await assetUserService.getActiveAssignments();
        setActiveAssignments(data.assignments || []);
      } else if (activeTab === 'all-assets') {
        await fetchAllAssets();
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, fetchAllAssets]);

  // Re-fetch assets when status filter changes
  useEffect(() => {
    if (activeTab === 'all-assets') {
      fetchAllAssets();
    }
  }, [assetStatusFilter, activeTab, fetchAllAssets]);

  useEffect(() => {
    if (!['asset_incharge', 'admin'].includes(user.role)) {
      return;
    }
    fetchData();
  }, [activeTab, user.role, fetchData]);

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    setApprovalData({ remarks: '' });
    setModalOpen(true);
  };

  const handleApproveSubmit = async () => {
    setLoading(true);
    try {
      await assetUserService.approveAssetRequest(selectedRequest._id, approvalData);
      alert('Request approved successfully');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to approve request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Enter rejection reason (optional):');
    if (reason === null) return;

    setLoading(true);
    try {
      await assetUserService.rejectAssetRequest(requestId, { rejectionReason: reason });
      alert('Request rejected');
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to reject request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (assignmentId) => {
    const remarks = prompt('Enter remarks (optional):');
    if (remarks === null) return;

    setLoading(true);
    try {
      await assetUserService.revokeAsset(assignmentId, { remarks });
      alert('Asset revoked successfully');
      fetchData();
    } catch (err) {
      alert(err.message || 'Failed to revoke asset');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = async (assignment) => {
    alert('Swap feature implementation - contact developer');
    // TODO: Implement swap dialog
  };

  return (
    <div className="asset-management-page">
      <AssistantSidebar user={null} />
      <div className="asset-management">
      <div className="management-header">
        <h1>Manage Asset Requests</h1>
        <p>Manage asset requests, user assignments, revok
e assets, and view inventory details all in one place.
        </p>
      </div>

      <div className="management-tabs">
        <button
          className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          Pending Requests ({pendingRequests.length})
        </button>
        <button
          className={`tab ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          Active Assignments ({activeAssignments.length})
        </button>
        <button
          className={`tab ${activeTab === 'all-assets' ? 'active' : ''}`}
          onClick={() => setActiveTab('all-assets')}
        >
          <FaBoxOpen style={{ marginRight: 6 }} /> All Assets
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {loading && <div className="loading">Loading...</div>}

      {/* Pending Requests Tab */}
      {activeTab === 'requests' && !loading && (
        <div className="tab-pane">
          {pendingRequests.length === 0 ? (
            <p className="empty">No pending requests</p>
          ) : (
            <div className="requests-table">
              {pendingRequests.map((request) => (
                <div key={request._id} className="request-row">
                  <div className="request-main">
                    <div className="request-title">{request.assetId?.assetName}</div>
                    <div className="request-details">
                      <span><strong>Requested by:</strong> {request.requestedBy?.name}</span>
                      <span><strong>Serial:</strong> {request.assetId?.serialNumber}</span>
                      <span><strong>Type:</strong> {request.requestType === 'swap_request' ? 'Swap' : 'New'}</span>
                      {request.reason && <span><strong>Reason:</strong> {request.reason}</span>}
                    </div>
                  </div>
                  <div className="request-actions">
                    <button className="btn-approve" onClick={() => handleApproveClick(request)}>
                      <FaCheck /> Approve
                    </button>
                    <button className="btn-reject" onClick={() => handleReject(request._id)}>
                      <FaTimes /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active Assignments Tab */}
      {activeTab === 'assignments' && !loading && (
        <div className="tab-pane">
          {activeAssignments.length === 0 ? (
            <p className="empty">No active assignments</p>
          ) : (
            <div className="assignments-table">
              {activeAssignments.map((assignment) => (
                <div key={assignment._id} className="assignment-row">
                  <div className="assignment-main">
                    <div className="assignment-title">{assignment.assetId?.assetName}</div>
                    <div className="assignment-details">
                      <span><strong>User:</strong> {assignment.userId?.name}</span>
                      <span><strong>Serial:</strong> {assignment.assetId?.serialNumber}</span>
                      <span><strong>Desk:</strong> {assignment.deskLocation || '—'}</span>
                      <span><strong>Assigned:</strong> {new Date(assignment.assignedDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="assignment-actions">
                    <button className="btn-swap" onClick={() => handleSwap(assignment)} title="Swap with another user">
                      <FaExchangeAlt /> Swap
                    </button>
                    <button className="btn-revoke" onClick={() => handleRevoke(assignment._id)}>
                      <FaTrash /> Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Assets Tab */}
      {activeTab === 'all-assets' && (
        <div className="tab-pane">
          <div className="all-assets-filters">
            <div className="asset-search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search by name, serial, brand..."
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchAllAssets(); }}
              />
              <button className="btn-search-sm" onClick={fetchAllAssets}>Search</button>
            </div>
            <select
              value={assetStatusFilter}
              onChange={(e) => { setAssetStatusFilter(e.target.value); }}
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="in_use">In Use</option>
              <option value="under_maintenance">Under Maintenance</option>
              <option value="disposed">Disposed</option>
            </select>
            <button className="btn-refresh-assets" onClick={fetchAllAssets} disabled={assetsLoading}>
              <FaSync className={assetsLoading ? 'spin' : ''} /> Refresh
            </button>
          </div>

          {assetsLoading && allAssets.length === 0 ? (
            <div className="loading">Loading assets...</div>
          ) : allAssets.length === 0 ? (
            <p className="empty">No assets found</p>
          ) : (
            <div className="all-assets-table-wrap">
              <table className="all-assets-table">
                <thead>
                  <tr>
                    <th>Asset Name</th>
                    <th>Category</th>
                    <th>Serial #</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Condition</th>
                    <th>Qty</th>
                    <th>Unit IDs</th>
                    <th>Value (₹)</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allAssets.map((asset) => {
                    const displayUnits = asset.units?.slice(0, 3) || [];
                    const moreUnitsCount = (asset.units?.length || 0) - 3;
                    
                    return (
                      <tr key={asset._id}>
                        <td><strong>{asset.assetName}</strong></td>
                        <td><span className="cat-badge">{asset.category}</span></td>
                        <td>{asset.serialNumber || '—'}</td>
                        <td>{asset.department || '—'}</td>
                        <td>
                          <span className={`status-pill-am status-${asset.status}`}>
                            {(asset.status || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          <span className={`condition-pill condition-${asset.condition}`}>
                            {asset.condition}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600, textAlign: 'center' }}>{asset.quantity || 1}</td>
                        <td>
                          <div className="unit-ids-cell">
                            {displayUnits.map(unit => (
                              <span key={unit.unitId} className={`unit-badge-sm ${unit.status === 'in_use' ? 'in-use' : ''}`}>
                                {unit.unitId}
                              </span>
                            ))}
                            {moreUnitsCount > 0 && (
                              <span className="units-more-badge" title={`+${moreUnitsCount} more units`}>
                                +{moreUnitsCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ fontWeight: 600, color: '#059669' }}>
                          {asset.purchaseCost ? `₹${asset.purchaseCost.toLocaleString()}` : '—'}
                        </td>
                        <td>
                          <div className="table-actions-cell" style={{ justifyContent: 'center' }}>
                            <button className="btn-table-action btn-view-edit">Edit</button>
                            <button className="btn-table-action btn-delete">Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Approval Modal */}
      {modalOpen && selectedRequest && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-bar">
              <h2>Review & Approve Request</h2>
              <button className="modal-close-btn" onClick={() => setModalOpen(false)}><FaTimes /></button>
            </div>
            <div className="modal-body">
              {/* Requester Info Section */}
              <div className="detail-section">
                <h4><FaUser className="section-icon" /> Requester Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{selectedRequest.requestedBy?.name || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedRequest.requestedBy?.email || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Department</span>
                    <span className="detail-value">{selectedRequest.requestedBy?.department || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Asset Info Section */}
              <div className="detail-section">
                <h4><FaBox className="section-icon" /> Asset Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Asset Name</span>
                    <span className="detail-value">{selectedRequest.assetId?.assetName || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Serial Number</span>
                    <span className="detail-value">{selectedRequest.assetId?.serialNumber || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Category</span>
                    <span className="detail-value">{selectedRequest.assetId?.category || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location</span>
                    <span className="detail-value">{selectedRequest.assetId?.labLocation || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Request Details Section */}
              <div className="detail-section">
                <h4><FaClipboardList className="section-icon" /> Request Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Request Type</span>
                    <span className="detail-value request-type-badge">
                      {selectedRequest.requestType === 'swap_request' ? 'Swap Request'
                        : selectedRequest.requestType === 'asset_breakdown' ? 'Breakdown'
                        : selectedRequest.requestType === 'asset_release' ? 'Release'
                        : selectedRequest.requestType === 'asset_extension' ? 'Extension'
                        : 'New Request'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Requested On</span>
                    <span className="detail-value">{selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleString() : '—'}</span>
                  </div>
                  {selectedRequest.requestDepartment && (
                    <div className="detail-item">
                      <span className="detail-label">Request Department</span>
                      <span className="detail-value">{selectedRequest.requestDepartment}</span>
                    </div>
                  )}
                  {selectedRequest.duration && (
                    <div className="detail-item">
                      <span className="detail-label"><FaClock style={{marginRight: 4}} /> Duration</span>
                      <span className="detail-value">{selectedRequest.duration.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {selectedRequest.startDate && (
                    <div className="detail-item">
                      <span className="detail-label"><FaCalendarAlt style={{marginRight: 4}} /> From Date</span>
                      <span className="detail-value">{new Date(selectedRequest.startDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedRequest.deskLocation && (
                    <div className="detail-item">
                      <span className="detail-label"><FaMapMarkerAlt style={{marginRight: 4}} /> Desk / Station</span>
                      <span className="detail-value">{selectedRequest.deskLocation}</span>
                    </div>
                  )}
                  {selectedRequest.swapWithUserId && (
                    <div className="detail-item">
                      <span className="detail-label">Swap With User</span>
                      <span className="detail-value">{selectedRequest.swapWithUserId?.name || '—'}</span>
                    </div>
                  )}
                  {selectedRequest.extendDuration && (
                    <div className="detail-item">
                      <span className="detail-label">Extension Duration</span>
                      <span className="detail-value">{selectedRequest.extendDuration.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                </div>
                {selectedRequest.reason && (
                  <div className="detail-reason">
                    <span className="detail-label"><FaInfoCircle style={{marginRight: 4}} /> Reason</span>
                    <p>{selectedRequest.reason}</p>
                  </div>
                )}
                {selectedRequest.breakdownDescription && (
                  <div className="detail-reason">
                    <span className="detail-label">Breakdown Description</span>
                    <p>{selectedRequest.breakdownDescription}</p>
                  </div>
                )}
              </div>

              {/* Remarks Input */}
              <div className="form-group">
                <label>Approval Remarks</label>
                <textarea
                  placeholder="Any additional notes for the user"
                  value={approvalData.remarks}
                  onChange={(e) => setApprovalData({ ...approvalData, remarks: e.target.value })}
                  rows="3"
                  className="input-field"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-submit" onClick={handleApproveSubmit} disabled={loading}>
                <FaCheck /> {loading ? 'Approving...' : 'Approve Request'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AssetManagement;
